(ns dashboard.entity.organization.db
  (:require [dashboard.effects :as effects]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [io.repliance.schemas.organization :as org]
            [malli.core :as m]
            [malli.error :as me]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]))

(rf/reg-sub
 ::organization
 (fn [db] (:organization db)))

(def view-id-organization :route.view/organization)
(def view-id-member :route.view/organization-member)
(def view-id-member-action :route.view/organization-member-action)

(defn breadcrumbs []
  [{:href (rfe/href :route.view/home)
    :label "Dashboard"}
   {:href (rfe/href view-id-organization)
    :label "Organizations"
    :active? true}])

;; ----------------------------------------------------------------------------
;; Events
;; ----------------------------------------------------------------------------

(rf/reg-event-fx
 ::perform-navigation
 (fn [_ [_ route-name params query]]
   {::effects/fx-redirect [route-name params query]}))

(defn perform-navigation
  [route-name params query]
  (rf/dispatch [::perform-navigation route-name params query]))

(rf/reg-event-db
 ::clear-form-errors
 (fn [db _]
   (assoc-in db [:organization :form-errors] nil)))

(defn clear-form-errors
  []
  (rf/dispatch [::clear-form-errors]))

;; ----------------------------------------------------------------------------
;;  API - Organization Members as persons
;; ----------------------------------------------------------------------------


(rf/reg-event-db
 ::get-organization-data-success
 (fn [db [_ organization]]
   (assoc-in db [:organization :data] organization)))

(rf/reg-event-fx
 ::set-organization-data-failure
 (fn [_ [_ message error]]
   {:fx [[:dispatch [::ui/set-error (str message ": " (select-keys error [:failure :status-text :status :original-text]))]]]}))

(rf/reg-event-fx
 ::fetch-organization
 (fn [{:keys [db]} [_]]
   {:db (assoc-in db [:organization :loading] true)
    :http-xhrio {:method          :get
                 :uri             "/api/organization"
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::get-organization-data-success]
                 :on-failure      [::set-organization-data-failure "Failed to get organization"]}}))

(defn fetch-organization
  []
  (rf/dispatch [::fetch-organization]))

(rf/reg-sub
 ::organization-members
 :<- [::organization]
 (fn [organization [_]]
   (get organization :data)))

(defn organization-members*
  []
  (rf/subscribe [::organization-members]))

;; ----------------------------------------------------------------------------
;;  API - Person's Revision History
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::fetch-person-revision-history-success
 (fn [db [_ revision-history]]
   (let [person-id (-> db :route :parameters :path :selected-person-id js/parseInt)]
     (assoc-in db [:organization person-id :history] revision-history))))

(rf/reg-event-fx
 ::fetch-person-revision-history
 (fn [{:keys [db]} [_ _]]
   (let [person-id (-> db :route :parameters :path :selected-person-id)]
    {:db (-> db
            (assoc-in [:organization :loading-history] true))
    :http-xhrio {:method          :get
                 :uri             (str "/api/organization/members/" person-id)
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::fetch-person-revision-history-success]
                 :on-failure      [::set-organization-data-failure "Failed to get revision history"]}})))

(defn fetch-person-revision-history
  []
  (rf/dispatch [::fetch-person-revision-history]))

(rf/reg-sub
 ::person-revision-history
 :<- [::organization]
 (fn [organization [_ person-id]]
   (get-in organization [person-id :history] [])))

(defn person-revision-history*
  [person-id]
  (rf/subscribe [::person-revision-history person-id]))

;; ----------------------------------------------------------------------------
;;  API - Save Member as Person
;; ----------------------------------------------------------------------------

(defn validate-humanize [data schema]
  (-> schema
      (m/explain data)
      (me/humanize {:errors me/default-errors})))

(rf/reg-sub
 ::form-errors
 :<- [::organization]
 (fn [organization]
   (get organization [:form-errors] [])))

(defn form-errors*
  []
  (rf/subscribe [::form-errors]))

(rf/reg-event-fx
 ::save-person-success
 (fn [{:keys [db]} [_ callback prevent-navigate-on-save member-data]]
   (callback member-data)
   {:db (-> db
            (assoc-in [:organization :form-errors] nil)
            (update-in [:organization :data]
                       (fn [coll]
                         (-> (remove
                              (fn [{:keys [person-id]}]
                                (= person-id (:person-id member-data)))
                              coll)
                             vec
                             (conj (dissoc member-data :create-new))))))
    :fx (when-not prevent-navigate-on-save
          [[:dispatch [::perform-navigation view-id-organization]]])}))

(rf/reg-event-fx
 ::save-person
 (fn [{:keys [db]} [_ person callback prevent-navigate-on-save]]
   (let [person (dissoc person
                        :create-new
                        :has-reports
                        :reports
                        :is-reports-to-updated)
         form-errors (validate-humanize person
                                        (if (:person-id person)
                                          org/Person
                                          org/NewPerson))]
     (cond-> {:db (-> db
                      (assoc-in [:organization :loading] true)
                      (assoc-in [:organization :form-errors] form-errors))}
       (empty? form-errors)
       (assoc :http-xhrio
              (when (empty? form-errors)
                (if (:person-id person)
                  {:method          :post
                   :uri             (str "/api/organization/members/"
                                         (:person-id person))
                   :params          {:person person}
                   :timeout         http-config/timeout
                   :format          http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success      [::save-person-success callback prevent-navigate-on-save]
                   :on-failure      [::set-organization-data-failure "Failed to update person"]}
                  {:method          :post
                   :uri             "/api/organization"
                   :params          {:person person}
                   :timeout         http-config/timeout
                   :format          http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success      [::save-person-success callback prevent-navigate-on-save]
                   :on-failure      [::set-organization-data-failure "Failed to save new person"]})))))))

(defn save-person
  [person callback prevent-navigate-on-save]
  (rf/dispatch [::save-person person callback prevent-navigate-on-save]))

;; ----------------------------------------------------------------------------
;;  API - Person's ASA Responsibility List
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::fetch-person-asa-responsibilities-success
 (fn [db [_ responsibilities]]
   (let [person-id (-> db :route :parameters :path :selected-person-id js/parseInt)]
     (assoc-in db [:organization person-id :responsibilities] responsibilities))))

(rf/reg-event-fx
 ::fetch-person-asa-responsibilities
 (fn [{:keys [db]} [_ _]]
   (let [person-id (-> db :route :parameters :path :selected-person-id js/parseInt)]
     {:db (assoc-in db [:organization :loading-responsibility] true)
      :http-xhrio {:method          :get
                   :uri             (str "/api/organization/members/" person-id "/responsibilities")
                   :timeout         http-config/timeout
                   :format          http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success      [::fetch-person-asa-responsibilities-success]
                   :on-failure      [::set-organization-data-failure "Failed to get responsibilities"]}})))

(defn fetch-person-asa-responsibilities
  []
  (rf/dispatch [::fetch-person-asa-responsibilities]))

(rf/reg-sub
 ::person-asa-responsibilities
 :<- [::organization]
 (fn [organization [_ person-id]]
   (get-in organization [person-id :responsibilities] [])))

(defn person-asa-responsibilities*
  [person-id]
  (rf/subscribe [::person-asa-responsibilities person-id]))

;; ----------------------------------------------------------------------------
;;  Responsibility Modal
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::set-responsibility-modal-open
 (fn [db [_ value]]
   (assoc-in db [:organization :responsibility-modal-open] value)))

(defn set-responsibility-modal-open
  [value]
  (rf/dispatch [::set-responsibility-modal-open value]))

(rf/reg-sub
 ::get-responsibility-modal-open
 :<- [::organization]
 (fn [organization]
   (get organization :responsibility-modal-open false)))

(defn get-responsibility-modal-open*
  []
  (rf/subscribe [::get-responsibility-modal-open]))